/**
 * WordPuzzleDisplay - Shared component for displaying word puzzle paths
 * Used in both the initial tutorial and puzzle archive
 */
import React from 'react';
import * as Lucide from 'lucide-react';

export interface WordPuzzleDisplayProps {
  /** The starting word */
  startWord: string;
  /** The goal/ending word */
  goalWord: string;
  /** Optional intermediate words to show */
  intermediateWords?: string[];
  /** Primary color for the display (used for goal word and border) */
  primaryColor: string;
  /** Font family to use */
  fontFamily?: string;
  /** Additional styles for the container */
  containerStyles?: React.CSSProperties;
  /** Whether to show question marks for unknown path */
  showUnknownPath?: boolean;
  /** Number of question marks to show (default: 3) */
  unknownPathLength?: number;
  /** Whether to capitalize words */
  capitalize?: boolean;
}

const capitalizeWord = (word: string): string => {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

/**
 * Shared component for displaying word puzzle paths with consistent styling
 */
export const WordPuzzleDisplay: React.FC<WordPuzzleDisplayProps> = ({
  startWord,
  goalWord,
  intermediateWords = [],
  primaryColor,
  fontFamily = 'Georgia, "Times New Roman", serif',
  containerStyles = {},
  showUnknownPath = false,
  unknownPathLength = 3,
  capitalize = true,
}) => {
  const displayWord = (word: string, isGoalWord: boolean = false) => {
    const text = capitalize ? capitalizeWord(word) : word;
    return (
      <span
        style={{
          fontWeight: isGoalWord ? "600" : "400",
          fontSize: isGoalWord ? "1.4rem" : "1rem",
          color: isGoalWord ? primaryColor : "rgba(0, 0, 0, 0.8)",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textAlign: "center",
          cursor: "default",
          flexShrink: 0,
        }}
      >
        {text}
      </span>
    );
  };

  const renderDivider = () => (
    <Lucide.ChevronRight
      size={16}
      style={{
        marginTop: "3px",
        color: "rgba(0, 0, 0, 0.6)",
        flexShrink: 0,
      }}
    />
  );

  const renderUnknownPath = () => {
    const elements: React.ReactNode[] = [];
    for (let i = 0; i < unknownPathLength; i++) {
      elements.push(
        <React.Fragment key={`unknown-${i}`}>
          {renderDivider()}
          <span
            style={{
              fontSize: "1.2rem",
              color: "rgba(0, 0, 0, 0.5)",
            }}
          >
            ?
          </span>
        </React.Fragment>
      );
    }
    elements.push(
      <React.Fragment key="unknown-final">
        {renderDivider()}
      </React.Fragment>
    );
    return elements;
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "0.3rem",
        borderRadius: "8px",
        padding: "1rem",
        border: `1px solid ${primaryColor}`,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        boxShadow: `0 2px 4px rgba(0, 0, 0, 0.2)`,
        fontFamily,
        ...containerStyles,
      }}
    >
      {/* Start word */}
      {displayWord(startWord)}

      {/* Intermediate words */}
      {intermediateWords.map((word, i) => (
        <React.Fragment key={`intermediate-${i}`}>
          {renderDivider()}
          {displayWord(word)}
        </React.Fragment>
      ))}

      {/* Unknown path (question marks) */}
      {showUnknownPath && renderUnknownPath()}

      {/* Divider before goal word if no intermediate words or unknown path */}
      {intermediateWords.length === 0 && !showUnknownPath && renderDivider()}

      {/* Goal word */}
      {displayWord(goalWord, true)}
    </div>
  );
};
